<template>
  <v-dialog v-model="dialog" max-width="700px">
    <v-card>
      <v-card-title>
        <span class="text-h6 font-weight-bold">
          {{ isEditMode ? 'Edit' : 'Add' }} Trade Order
        </span>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.stock_id"
                :items="stockOptions"
                item-title="label"
                item-value="value"
                label="Select Stock"
                variant="outlined"
                required
              />
            </v-col>

            <v-col cols="12" sm="3">
              <v-select
                v-model="form.qty_type"
                :items="qtyType"
                label="QTY Type"
                item-title="label"
                item-value="value"
                variant="outlined"
              />
            </v-col>

            <v-col cols="12" sm="3">
              <v-text-field
                v-model.number="form.qty"
                label="Quantity"
                type="number"
                variant="outlined"
                required
              />
            </v-col>

            <!-- Trade Type -->
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.trade_type"
                :items="tradeTypeOptions"
                label="Trade Type"
                variant="outlined"
                required
              />
            </v-col>

            <!-- Rule Composition -->
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.rule_target"
                :items="ruleTargets"
                label="Target"
                item-title="label"
                item-value="value"
                variant="outlined"
              />
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="form.rule_operator"
                :items="ruleOperators"
                label="Operator"
                item-title="label"
                item-value="value"
                variant="outlined"
              />
            </v-col>

            <v-col cols="12" v-if="form.rule_operator === 'between'">
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="form.rule_value.from"
                    label="From"
                    type="number"
                    variant="outlined"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="form.rule_value.to"
                    label="To"
                    type="number"
                    variant="outlined"
                  />
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" v-else>
              <v-text-field
                v-model.number="form.rule_value.value"
                label="Value"
                type="number"
                variant="outlined"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-select
                v-model="form.status"
                :items="statusOptions"
                label="Status"
                variant="outlined"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" @click="submit">{{ isEditMode ? 'Update' : 'Create' }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  modelValue: Boolean,
  editData: Object,
  stockOptions: Array,
})
const emit = defineEmits(['update:modelValue', 'submit'])

const dialog = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const isEditMode = computed(() => !!props.editData?.trade_order_id)

const statusOptions = [
  'Pending',
  'Executed',
  'Partially Executed',
  'Hold',
  'Cancelled',
  'Rejected',
  'Expired'
];
const tradeTypeOptions = ['Buy', 'Sell']

const ruleTargets = [
  { label: '52 Week Low', value: 'fifty_two_week_low' },
  { label: '52 Week High', value: 'fifty_two_week_high' },
  { label: 'Price', value: 'price' },
  { label: 'Profit %', value: 'profit' },
  { label: 'Loss %', value: 'loss' },
]

const ruleOperators = [
  { label: '=', value: '=' },
  { label: '>', value: '>' },
  { label: '<', value: '<' },
  { label: 'Between', value: 'between' },
]

const qtyType = [
  { label: 'Fix Qty', value: '1' },
  { label: 'Percentage', value: '2' },
]

const form = ref({
  stock_id: '',
  qty: 0,
  trade_type: '', // NEW FIELD
  rule_target: '',
  rule_operator: '',
  rule_value: {
    value: null,
    from: null,
    to: null,
  },
  status: '',
})

watch(
  () => props.editData,
  (newVal) => {
    if (newVal) {
      const parsedRule = JSON.parse(newVal.rule || '{}')
      form.value = {
        stock_id: newVal.stock_id || 0,
        qty: newVal.qty || 0,
        trade_type: newVal.trade_type || '', // <-- Load trade_type
        rule_target: parsedRule.target || '',
        rule_operator: parsedRule.rule || '',
        rule_value: parsedRule.value || { value: null, from: null, to: null },
        status: newVal.status || '',
      }
    } else {
      form.value = {
        stock_id: 0,
        qty: 0,
        trade_type: '',
        rule_target: '',
        rule_operator: '',
        rule_value: {
          value: null,
          from: null,
          to: null,
        },
        status: '',
      }
    }
  },
  { immediate: true }
)

function close() {
  emit('update:modelValue', false)
}

function submit() {
  const composedRule = {
    target: form.value.rule_target || '',
    rule: form.value.rule_operator || '',
    value: form.value.rule_value || { value: null, from: null, to: null },
    action: form.value.trade_type || '',
  }

  // Cast values here before sending to backend
  const payload = {
    stock_id: Number(form.value.stock_id),        // cast to Number (Int)
    qty: Number(form.value.qty),                   // cast to Number (Int)
    status: form.value.status,
    rule: composedRule,
    trade_type: form.value.trade_type,             // pass as is (string)
  }

  emit('submit', payload)
  close()
}
</script>